import { Modal } from "antd";
import { useOrderingMessage } from "../../useOrderingMessage";
import { PercentageOutlined } from "@ant-design/icons";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { getApiErrorMessage } from "../../../../../utils/translations";
import { posModalStyles as modalStyles, ModalHeader } from "../../modalPrimitives";
import { ItemDiscountRow } from "./ItemDiscountRow";
import { DiscountPercentPicker } from "./DiscountPercentPicker";
import { SelectAllBar } from "./SelectAllBar";
import { ItemDiscountsFooter } from "./ItemDiscountsFooter";
import { DiscountModeTabs, type DiscountMode } from "./DiscountModeTabs";
import { BillAmountPicker } from "./BillAmountPicker";
import { BillAmountSummary } from "./BillAmountSummary";
import type { RowPercentChoice } from "./RowPercentOptions";
import {
  buildDiscountRows,
  mostCommonPercent,
  planRowDiscount,
  roundToCent,
  type DiscountRow,
} from "./itemDiscountMath";
import {
  appliedBillDiscount,
  clampAmount,
  discountableTotal,
  effectiveAmount,
  requestedBillDiscount,
} from "./billAmountDiscount";
import {
  useApplyAmountDiscount,
  useApplyItemDiscounts,
} from "../../../../../hooks/useBills";
import { useViewport } from "../../../../../hooks/useViewport";
import { useBillDiscountLocked } from "../../../../../hooks/useEdgeMode";
import type { BillOut } from "../../../../../api/bills";

interface ItemDiscountsModalProps {
  open: boolean;
  onClose: () => void;
  billId: number | null;
  bill: BillOut | undefined;
}

/** Общият процент при отваряне без отстъпки — най-честата е на два клика. */
const DEFAULT_PERCENT = 10;
/** „Безплатно" на реда. */
const FREE_PERCENT = 100;

/** id на ред → стойност. Ред без запис не участва / следва общата. */
type PerRow = Record<number, number>;
/** id на ред → собственият процент (undefined = общата) и бройките. */
type PerRowChoice = Record<number, { ownPercent?: number; quantity: number }>;

/** Отстъпки по сметка — БЕЗ предварително избран продукт. Два вида (табовете
 *  най-горе): „По продукти" и „На цялата сметка".
 *
 *  „По продукти": модалът показва КРАЙНОТО състояние на всеки ред: избраните
 *  бройки (степер −N/max+) ще са с процента на реда, а неизбраните — без
 *  отстъпка. Процентът на реда е общият горе или собствен (chip-ът на реда →
 *  „За този ред"), затова една сметка събира няколко различни отстъпки и
 *  редове без отстъпка на един запис. Праща се само разликата спрямо
 *  сегашното (`planRowDiscount`).
 *
 *  „На цялата сметка": сума в €, извадена от сбора след отстъпките по ред
 *  (BE `service_bill_discount.py`; на бона — един ред „Отстъпка"). Само в
 *  облака — в локален режим табът е заключен с подсказка. */
export const ItemDiscountsModal = ({
  open,
  onClose,
  billId,
  bill,
}: ItemDiscountsModalProps) => {
  const { t, i18n } = useTranslation();
  const message = useOrderingMessage();
  const { isMobile, isNarrow, isTablet, isDesktop } = useViewport();
  const applyItemDiscountsMutation = useApplyItemDiscounts();
  const applyAmountDiscountMutation = useApplyAmountDiscount();
  // „На цялата сметка" без интернет — само с каса, която я умее (docs/edge.md).
  const billDiscountLocked = useBillDiscountLocked();

  const [mode, setMode] = useState<DiscountMode>("items");
  const [billAmount, setBillAmount] = useState(0);
  const [generalPercent, setGeneralPercent] = useState(DEFAULT_PERCENT);
  const [selectedQuantities, setSelectedQuantities] = useState<PerRow>({});
  const [ownPercents, setOwnPercents] = useState<PerRow>({});
  const [expandedRowId, setExpandedRowId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  // Изборът на реда отпреди отметката „Безплатно" — махнатата отметка го връща.
  const beforeFreeRef = useRef<PerRowChoice>({});

  const rows = useMemo(
    () => buildDiscountRows(bill, i18n.language, t("common.product")),
    [bill, i18n.language, t],
  );
  const selectableRows = useMemo(
    () => rows.filter((row) => !row.locked),
    [rows],
  );

  // „На цялата сметка": сборът на редовете сега (с отстъпките им по ред, без
  // тази на сметката) — и таванът на отстъпката (заключените цени също).
  const currentTotals = useMemo(
    () => rows.map((row) => row.currentTotal),
    [rows],
  );
  const rowsTotal = roundToCent(
    currentTotals.reduce((sum, value) => sum + value, 0),
  );
  const billDiscountMax = discountableTotal(currentTotals);
  const requestedAmount = requestedBillDiscount(bill);

  // При отваряне редовете с отстъпка са избрани с цялото си количество.
  // Общият процент е най-честият им; редовете с друг процент го пазят като
  // собствен, за да не ги пренапише смяна на общия.
  useEffect(() => {
    if (!open) return;
    const general = mostCommonPercent(rows) ?? DEFAULT_PERCENT;
    const discountedRows = rows.filter((row) => row.currentPercent > 0);
    setGeneralPercent(general);
    setSelectedQuantities(
      Object.fromEntries(
        discountedRows.map((row) => [row.billElementItemId, row.quantity]),
      ),
    );
    setOwnPercents(
      Object.fromEntries(
        discountedRows
          .filter((row) => row.currentPercent !== general)
          .map((row) => [row.billElementItemId, row.currentPercent]),
      ),
    );
    setExpandedRowId(null);
    beforeFreeRef.current = {};
    // Сметка с отстъпка в € се отваря на нейния таб (в локален режим с каса,
    // която не я умее, той е заключен — тогава „По продукти").
    setMode(requestedAmount > 0 && !billDiscountLocked ? "bill" : "items");
    setBillAmount(clampAmount(requestedAmount, billDiscountMax));
    // Само при отваряне — опресняването на сметката след запис не бива да
    // пренаписва избора, докато модалът се затваря.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const selectedQtyOf = (row: DiscountRow) =>
    selectedQuantities[row.billElementItemId] ?? 0;
  const hasOwnPercent = (row: DiscountRow) =>
    ownPercents[row.billElementItemId] !== undefined;
  const percentOf = (row: DiscountRow) =>
    ownPercents[row.billElementItemId] ?? generalPercent;

  const setQuantity = (row: DiscountRow, quantity: number) =>
    setSelectedQuantities((prev) => ({
      ...prev,
      [row.billElementItemId]: Math.max(0, Math.min(row.quantity, quantity)),
    }));

  const changeQuantity = (row: DiscountRow, delta: number) =>
    setQuantity(row, selectedQtyOf(row) + delta);

  // Изборът от „За този ред": „Без" маха реда от отстъпката, процент (или
  // „Обща") го избира с цялото количество, ако още не е избран.
  const pickRowPercent = (row: DiscountRow, choice: RowPercentChoice) => {
    const id = row.billElementItemId;
    setOwnPercents((prev) => {
      const next = { ...prev };
      if (choice === null || choice === 0) delete next[id];
      else next[id] = choice;
      return next;
    });
    if (choice === 0) setQuantity(row, 0);
    else if (selectedQtyOf(row) === 0) setQuantity(row, row.quantity);
    setExpandedRowId(null);
  };

  const isFreeRow = (row: DiscountRow) =>
    selectedQtyOf(row) > 0 && percentOf(row) === FREE_PERCENT;

  // „Безплатно" = собствени 100% (неизбран ред — с цялото количество).
  // Махнатата отметка връща реда както е бил преди нея; без такъв запис
  // (отворен направо на 100%) — „Без".
  const toggleFree = (row: DiscountRow) => {
    const id = row.billElementItemId;
    if (!isFreeRow(row)) {
      beforeFreeRef.current[id] = {
        ownPercent: ownPercents[id],
        quantity: selectedQtyOf(row),
      };
      pickRowPercent(row, FREE_PERCENT);
      return;
    }
    const before = beforeFreeRef.current[id];
    delete beforeFreeRef.current[id];
    if (!before) {
      pickRowPercent(row, 0);
      return;
    }
    setOwnPercents((prev) => {
      const next = { ...prev };
      if (before.ownPercent === undefined) delete next[id];
      else next[id] = before.ownPercent;
      return next;
    });
    setQuantity(row, before.quantity);
  };

  const allSelected =
    selectableRows.length > 0 &&
    selectableRows.every((row) => selectedQtyOf(row) >= row.quantity);

  // Собствените проценти остават — повторното „Избери всички" ги връща.
  const toggleSelectAll = () =>
    setSelectedQuantities(
      allSelected
        ? {}
        : Object.fromEntries(
            selectableRows.map((row) => [row.billElementItemId, row.quantity]),
          ),
    );

  const removeAll = () => {
    setSelectedQuantities({});
    setOwnPercents({});
    setExpandedRowId(null);
  };

  const plans = rows.map((row) =>
    planRowDiscount(row, selectedQtyOf(row), percentOf(row)),
  );
  const changedLines = plans.flatMap((plan) => plan.lines);
  // Отстъпката в € на сметката остава и след отстъпките по ред — вади се от
  // новия им сбор (не повече от него, като в BE).
  const billCutAfterRows = effectiveAmount(
    requestedAmount,
    discountableTotal(plans.map((plan) => plan.total)),
  );
  // „Преди" е ВИНАГИ сумата по менюто; „след" — това, което ще се плати.
  const totalBefore = rows.reduce((sum, row) => sum + row.menuTotal, 0);
  const totalAfter =
    plans.reduce((sum, plan) => sum + plan.total, 0) - billCutAfterRows;

  const selectedRows = selectableRows.filter((row) => selectedQtyOf(row) > 0);
  const discountedCount = selectedRows.filter(
    (row) => percentOf(row) > 0,
  ).length;
  const ownPercentCount = selectedRows.filter(hasOwnPercent).length;

  const isBillMode = mode === "bill";
  const billAmountChanged =
    Math.round(billAmount * 100) !== Math.round(requestedAmount * 100);
  const canSave =
    !!billId && (isBillMode ? billAmountChanged : changedLines.length > 0);

  const handleSave = useCallback(async () => {
    if (!billId || !canSave) return;
    setSaving(true);
    try {
      if (isBillMode) {
        await applyAmountDiscountMutation.mutateAsync({
          billId,
          amount: billAmount.toFixed(2),
        });
        message.success(
          t(
            billAmount > 0
              ? "itemDiscounts.billApplySuccess"
              : "itemDiscounts.billRemoveSuccess",
          ),
        );
      } else {
        await applyItemDiscountsMutation.mutateAsync({
          billId,
          data: { billId, lines: changedLines },
        });
        message.success(t("itemDiscounts.applySuccess"));
      }
      onClose();
    } catch (err: unknown) {
      message.error(getApiErrorMessage(err, t("common.error")));
    } finally {
      setSaving(false);
    }
  }, [
    billId,
    canSave,
    isBillMode,
    billAmount,
    changedLines,
    applyAmountDiscountMutation,
    applyItemDiscountsMutation,
    message,
    t,
    onClose,
  ]);

  // Esc не прескача нива: първо свива разгънатия ред, после затваря модала
  // (затова antd `keyboard` е изключен). Enter записва — само на десктоп.
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // preventDefault = „Esc е консумиран" за глобалната Esc-навигация.
        e.preventDefault();
        if (expandedRowId !== null) setExpandedRowId(null);
        else onClose();
      } else if (e.key === "Enter" && isDesktop) {
        e.preventDefault();
        if (canSave && !saving) handleSave();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, isDesktop, expandedRowId, canSave, saving, handleSave, onClose]);

  const bodyPadClass = isNarrow ? "px-4" : "px-6";

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      keyboard={false}
      destroyOnHidden
      width={isMobile ? undefined : isTablet ? 520 : 640}
      styles={modalStyles}
      className="max-w-[calc(100vw-32px)]"
    >
      <div className="flex max-h-[88vh] flex-col bg-pos-card text-pos-ink">
        <ModalHeader
          icon={<PercentageOutlined />}
          title={t("itemDiscounts.title")}
          subtitle={t("itemDiscounts.subtitle", {
            name: bill?.name ?? "",
            total: parseFloat(bill?.total_amount ?? "0").toFixed(2),
          })}
          onClose={onClose}
          isNarrow={isNarrow}
        />

        {/* Видът най-горе, под него изборът му: общият процент (за избраните
            редове без собствен) или сумата в € на цялата сметка */}
        <div
          className={`${bodyPadClass} flex flex-col gap-4 border-b border-pos-line2 bg-pos-panel py-4`}
        >
          <DiscountModeTabs
            mode={mode}
            onChange={setMode}
            billLockedHint={billDiscountLocked ? t("edge.unavailableOffline") : undefined}
          />
          {isBillMode ? (
            <BillAmountPicker
              amount={billAmount}
              onChange={setBillAmount}
              max={billDiscountMax}
              autoFocus={open && isDesktop}
              isNarrow={isNarrow}
            />
          ) : (
            <>
              <DiscountPercentPicker
                percent={generalPercent}
                onChange={setGeneralPercent}
                autoFocus={open && isDesktop}
                ownPercentCount={ownPercentCount}
                isNarrow={isNarrow}
              />
              {requestedAmount > 0 && (
                <p className="-mt-2 m-0 text-[12px] font-medium text-pos-muted">
                  {t("itemDiscounts.billKeptNote", {
                    amount: appliedBillDiscount(bill).toFixed(2),
                  })}
                </p>
              )}
            </>
          )}
        </div>

        {isBillMode ? (
          <div className={`${bodyPadClass} min-h-0 flex-1 overflow-y-auto py-4`}>
            <BillAmountSummary
              rowsTotal={rowsTotal}
              amount={billAmount}
              isEmpty={rows.length === 0}
            />
          </div>
        ) : (
          <>
            <SelectAllBar
              allSelected={allSelected}
              disabled={selectableRows.length === 0}
              discountedCount={discountedCount}
              selectableCount={selectableRows.length}
              canRemoveAll={selectedRows.length > 0}
              onToggleSelectAll={toggleSelectAll}
              onRemoveAll={removeAll}
              isNarrow={isNarrow}
            />

            <div
              className={`${bodyPadClass} flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto py-3`}
            >
              {rows.length === 0 && (
                <p className="m-0 py-6 text-center text-sm text-pos-muted">
                  {t("itemDiscounts.emptyBill")}
                </p>
              )}
              {rows.map((row) => (
                <ItemDiscountRow
                  key={row.billElementItemId}
                  row={row}
                  selectedQty={selectedQtyOf(row)}
                  percent={percentOf(row)}
                  hasOwnPercent={hasOwnPercent(row)}
                  generalPercent={generalPercent}
                  expanded={expandedRowId === row.billElementItemId}
                  onToggleExpanded={() =>
                    setExpandedRowId((current) =>
                      current === row.billElementItemId
                        ? null
                        : row.billElementItemId,
                    )
                  }
                  onPickPercent={(choice) => pickRowPercent(row, choice)}
                  isFree={isFreeRow(row)}
                  onToggleFree={() => toggleFree(row)}
                  onQuantityChange={(delta) => changeQuantity(row, delta)}
                  lockedHint={t("ordering.lockedPriceNoDiscount")}
                  customPriceHint={t("itemDiscounts.customPriceHint")}
                  isNarrow={isNarrow}
                />
              ))}
            </div>
          </>
        )}

        {/* „На цялата сметка": преди е сборът на редовете, след — без сумата */}
        <ItemDiscountsFooter
          totalBefore={isBillMode ? rowsTotal : totalBefore}
          totalAfter={isBillMode ? rowsTotal - billAmount : totalAfter}
          canSave={canSave}
          saving={saving}
          onBack={onClose}
          onSave={handleSave}
          isNarrow={isNarrow}
          isDesktop={isDesktop}
        />
      </div>
    </Modal>
  );
};
